import { createContext, type PropsWithChildren, useContext, useEffect, useMemo } from 'react'

import { useGetEmployersRenewalsRenewalConfigsIdQuoteResults as useGetRenewalQuoteResults } from 'utils/web_api'
import { type EmployersRenewalsRenewalConfig as RenewalConfig, type EmployersRenewalsRenewalQuoteResults as RenewalQuoteResults } from 'utils/web_api.schemas'

import { useEmployerRenewalConfig } from './EmployerRenewalConfigProvider'

type QuoteResultsContextType = {
  isProcessing: boolean
  isError: boolean
  quoteResults?: RenewalQuoteResults
  isLoadingQuoteResults: boolean
}

const QuoteResultsContext = createContext<QuoteResultsContextType>({
  isProcessing: false,
  isError: false,
  isLoadingQuoteResults: false,
})

export function useQuoteResults () {
  return useContext(QuoteResultsContext)
}

const areQuotesProcessing = (employerRenewalConfig?: RenewalConfig) => {
  if (!employerRenewalConfig) {
    return false
  }

  const terminalStates = ['processed', 'failed']
  const currentYearQuoteStatus = employerRenewalConfig?.current_year_quote?.status
  const renewalYearQuoteStatus = employerRenewalConfig?.renewal_year_quote?.status

  return !terminalStates.includes(currentYearQuoteStatus ?? '') ||
    !terminalStates.includes(renewalYearQuoteStatus ?? '')
}

const isProcessingError = (employerRenewalConfig?: RenewalConfig) => {
  if (!employerRenewalConfig) {
    return false
  }

  return employerRenewalConfig?.current_year_quote?.status === 'failed' ||
    employerRenewalConfig?.renewal_year_quote?.status === 'failed'
}

const areQuotesProcessed = (employerRenewalConfig?: RenewalConfig) => {
  if (!employerRenewalConfig) {
    return false
  }

  return employerRenewalConfig?.current_year_quote?.status === 'processed' &&
    employerRenewalConfig?.renewal_year_quote?.status === 'processed'
}

export function QuoteResultsProvider ({ children }: PropsWithChildren) {
  const { employerRenewalConfig, refetch } = useEmployerRenewalConfig()

  const isProcessing = useMemo(() => {
    return areQuotesProcessing(employerRenewalConfig)
  }, [employerRenewalConfig])

  const isError = useMemo(() => {
    return isProcessingError(employerRenewalConfig)
  }, [employerRenewalConfig])

  const isProcessed = useMemo(() => {
    return areQuotesProcessed(employerRenewalConfig)
  }, [employerRenewalConfig])

  const { data: quoteResults, isLoading: isLoadingQuoteResults } = useGetRenewalQuoteResults(employerRenewalConfig?.id ?? '', {
    query: {
      enabled: isProcessed && !!employerRenewalConfig?.id,
    },
  })

  useEffect(() => {
    if (isProcessing) {
      const interval = setInterval(() => {
        refetch()
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [isProcessing, refetch])

  return (
    <QuoteResultsContext.Provider value={{
      isProcessing,
      isError,
      quoteResults,
      isLoadingQuoteResults,
    }}
    >
      {children}
    </QuoteResultsContext.Provider>
  )
}
